<template>
  <!--
    Aba "Documentos" (G1 · Tela 2 · B): fila à esquerda (médico → documentos, ordem do BE, D23),
    documento aberto à direita. Depois de decidir, segue para o próximo Em Análise.
  -->
  <div class="flex flex-col gap-y-4 text-left" data-testid="document-review-split-view">
    <EmptyState
      v-if="isUnavailable"
      :icon="mdiFileDocumentRemoveOutline"
      title="Revisão de documentos indisponível"
      description="A revisão de documentos ainda não foi liberada neste ambiente."
      variant="dashed"
    />
    <template v-else>
      <DocumentQueueFilterBar :filters="filters" @change="store.setFilters" />

      <div
        v-if="isLoading"
        class="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4 min-h-[560px]"
        data-testid="document-review-skeleton"
      >
        <div class="flex flex-col gap-2 rounded-lg border border-[#d8d8e2] p-3 animate-pulse">
          <div v-for="n in 7" :key="n" class="flex flex-col gap-1.5 py-2">
            <div class="h-3.5 w-40 bg-slate-200 rounded" />
            <div class="h-3 w-56 bg-slate-200 rounded" />
          </div>
        </div>
        <div class="rounded-lg border border-[#d8d8e2] bg-[#f0f0f4] animate-pulse" />
      </div>

      <ReportErrorState
        v-else-if="loadError"
        :title="QUEUE_LOAD_ERROR"
        :message="loadErrorMessage"
        @retry="store.fetchQueue()"
      />

      <EmptyState
        v-else-if="!groups.length"
        :icon="mdiFileDocumentCheckOutline"
        :title="
          isFiltered
            ? 'Nenhum documento com esses filtros'
            : 'Fila zerada — nada aguardando revisão'
        "
        :description="
          isFiltered
            ? 'Ajuste ou limpe os filtros para ver mais documentos.'
            : 'Quando um médico enviar documentos pelo Drive, eles aparecem aqui como Em Análise.'
        "
        variant="dashed"
      >
        <GhostButton
          v-if="isFiltered"
          text="Limpar filtros"
          width="180px"
          data-testid="document-review-clear-filters"
          @click="clearFilters"
        />
      </EmptyState>

      <div v-else class="grid grid-cols-1 lg:grid-cols-[340px_minmax(0,1fr)] gap-4 items-start">
        <aside
          class="flex flex-col rounded-lg border border-[#d8d8e2] bg-white overflow-hidden lg:sticky lg:top-2"
        >
          <div class="flex items-center justify-between px-3 py-2 bg-[#f0f0f4] text-[12px]">
            <span class="font-bold text-textPrimary">
              {{ inReviewCount }} em análise · {{ groups.length }}
              {{ groups.length === 1 ? 'médico' : 'médicos' }}
            </span>
            <label class="flex items-center gap-1 cursor-pointer text-[#52526f]">
              <input
                v-model="onlyInReview"
                type="checkbox"
                class="accent-[#2f7f91]"
                data-testid="document-queue-only-in-review"
              />
              só Em Análise
            </label>
          </div>
          <div
            ref="listRef"
            class="max-h-[640px] overflow-y-auto"
            data-testid="document-queue-list"
          >
            <section
              v-for="group in groups"
              :key="group.professional.userId"
              class="border-t border-[#e8e8ed] first:border-t-0"
            >
              <div class="flex flex-col gap-1 px-3 pt-2.5 pb-1.5">
                <div class="flex items-center justify-between gap-2">
                  <span class="text-[13px] font-bold text-textPrimary truncate">
                    {{ group.professional.name }}
                  </span>
                  <WaitingSince :since="group.professional.waitingSince" />
                </div>
                <ProfessionalTags :tags="group.professional.tags" />
              </div>
              <ul>
                <li
                  v-for="entry in group.entries"
                  :key="entry.document.documentId"
                  class="flex items-center gap-2 pl-3 pr-2 py-1.5 cursor-pointer border-l-[3px]"
                  :class="
                    entry.document.documentId === activeId
                      ? 'bg-primary5 border-l-primary30'
                      : 'border-l-transparent hover:bg-[#fafafa]'
                  "
                  :data-testid="`document-queue-item-${entry.document.documentId}`"
                  @click="activeId = entry.document.documentId"
                >
                  <input
                    type="checkbox"
                    class="accent-[#2f7f91]"
                    :disabled="!canDecide(entry)"
                    :checked="selectedIds.includes(entry.document.documentId)"
                    :aria-label="`Selecionar ${entry.document.name} de ${entry.professional.name}`"
                    :data-testid="`document-queue-select-${entry.document.documentId}`"
                    @click.stop
                    @change="toggleSelection(entry.document.documentId)"
                  />
                  <span class="flex-1 text-[12px] text-textPrimary truncate">
                    {{ entry.document.name }}
                  </span>
                  <DocumentStatusBadge
                    :status="entry.document.status"
                    :is-legacy="entry.document.isLegacy"
                  />
                </li>
              </ul>
            </section>
          </div>
          <div class="p-2 border-t border-[#e8e8ed]">
            <QueueBulkBar
              v-if="selectedIds.length"
              :selected-count="selectedIds.length"
              :pending-count="inReviewCount"
              pending-label="em análise"
              :primary-label="`Aprovar ${selectedIds.length}`"
              :actions="BULK_ACTIONS"
              test-id="document-queue-bulk"
              @primary="openApprove(selectedEntries)"
              @action="openReject(selectedEntries)"
              @clear="selectedIds = []"
            />
            <p v-else class="text-[11px] text-[#7f7f8e] px-1">
              Marque documentos Em Análise para aprovar ou reprovar em lote.
            </p>
          </div>
        </aside>

        <section
          v-if="active"
          class="flex flex-col gap-4 rounded-lg border border-[#d8d8e2] bg-white p-4"
          data-testid="document-review-active"
        >
          <header class="flex flex-wrap items-start justify-between gap-3">
            <div class="flex items-start gap-3 min-w-0">
              <!-- Foto pela lógica de avatar existente (TL 2026-10-15, R27 revisado). -->
              <ProtectedAvatar
                :src="activeAvatarSrc"
                :size="40"
                data-testid="document-review-avatar"
              >
                <img
                  src="@/assets/icons/doctorAvatar.svg"
                  alt="Avatar do médico"
                  class="w-full h-full rounded-full object-cover"
                  data-testid="document-review-avatar-fallback"
                />
              </ProtectedAvatar>
              <div class="min-w-0">
                <p class="text-base font-bold text-textPrimary">{{ active.professional.name }}</p>
                <p class="text-xs text-[#52526f]">{{ professionalSubtitle }}</p>
                <p v-if="activeDetail?.nextStation" class="text-xs font-bold text-alert">
                  Próximo plantão {{ formatStationStart(activeDetail.nextStation.start) }} —
                  {{ activeDetail.nextStation.scheduleName }}
                </p>
              </div>
            </div>
            <div class="flex items-center gap-2 shrink-0">
              <span class="text-xs text-[#7f7f8e]"
                >{{ position + 1 }} de {{ sequence.length }}</span
              >
              <OutlineButton
                text="Anterior"
                width="auto"
                height="32px"
                :prepend-icon="mdiChevronLeft"
                :disabled="position <= 0"
                data-testid="document-review-previous"
                @click="move(-1)"
              />
              <FilledButton
                text="Próximo"
                width="auto"
                height="32px"
                :append-icon="mdiChevronRight"
                :disabled="position >= sequence.length - 1"
                data-testid="document-review-next"
                @click="move(1)"
              />
            </div>
          </header>
          <DocumentReviewPanel
            :entry="active"
            :detail="activeDetail"
            :is-loading-detail="loadingDetailFor === active.professional.userId"
            show-shortcuts
            @approve="openApprove([active])"
            @reject="openReject([active])"
            @undo="requestUndo"
          />
        </section>
      </div>
    </template>

    <ApproveDocumentModal
      v-model="approveOpen"
      :items="approveItems"
      :is-saving="isSaving"
      @confirm="confirmApprove"
    />
    <RejectDocumentModal
      v-model="rejectOpen"
      :items="rejectItems"
      :is-saving="isSaving"
      @confirm="confirmReject"
    />
    <ConfirmDeleteAction
      :show="undoTarget !== null"
      title="Desfazer decisão?"
      :description="undoDescription"
      text-button="Desfazer decisão"
      @close-modal="undoTarget = null"
      @handle-confirm="confirmUndo"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toastification'
import {
  mdiChevronLeft,
  mdiChevronRight,
  mdiFileDocumentCheckOutline,
  mdiFileDocumentRemoveOutline
} from '@mdi/js'

import ProtectedAvatar from '@/components/designSystem/AvatarUser/ProtectedAvatar.vue'
import EmptyState from '@/components/designSystem/EmptyState/EmptyState.vue'
import ReportErrorState from '@/components/designSystem/ErrorState/ReportErrorState.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import GhostButton from '@/components/designSystem/Buttons/GhostButton/index.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import ConfirmDeleteAction from '@/components/designSystem/modals/confirm.delete.action.vue'
import QueueBulkBar from '@/components/designSystem/QueueViews/QueueBulkBar.vue'
import type { QueueBulkAction } from '@/components/designSystem/QueueViews/types'
import DocumentStatusBadge from '@/components/ProfessionalDocuments/DocumentStatusBadge.vue'
import { useErrorToast } from '@/composables/useErrorToast'
import { resolveAvatarSrc } from '@/utils/avatar'
import { EMPTY_REVIEW_FILTERS, useDocumentReviewStore } from '@/pinia-store/useDocumentReviewStore'
import type { ApproveTarget, DecisionTarget } from '@/pinia-store/useDocumentReviewStore'
import {
  CATALOG_LOAD_ERROR,
  useProfessionalDocumentCatalogStore
} from '@/pinia-store/useProfessionalDocumentCatalogStore'
import { ProfessionalDocumentStatus } from '@/types/professional-documents.types'
import type {
  BulkDecisionResponse,
  RejectVersionPayload
} from '@/types/professional-documents.types'

import {
  APPROVE_ERROR,
  DETAIL_LOAD_ERROR,
  QUEUE_LOAD_ERROR,
  REJECT_ERROR,
  UNDO_ERROR
} from '../constants'
import {
  decisionSummary,
  firstInReviewId,
  flattenQueue,
  formatStationStart,
  isInReview,
  nextInReviewId
} from '../model'
import type { DecisionVerb, ReviewQueueEntry } from '../model'
import { useDocumentReviewShortcuts } from '../composables/useDocumentReviewShortcuts'
import ApproveDocumentModal from './ApproveDocumentModal.vue'
import type { ApproveModalItem } from './ApproveDocumentModal.vue'
import DocumentQueueFilterBar from './DocumentQueueFilterBar.vue'
import DocumentReviewPanel from './DocumentReviewPanel.vue'
import ProfessionalTags from './ProfessionalTags.vue'
import RejectDocumentModal from './RejectDocumentModal.vue'
import type { RejectModalItem } from './RejectDocumentModal.vue'
import WaitingSince from './WaitingSince.vue'

const BULK_ACTIONS: QueueBulkAction[] = [{ key: 'reject', label: 'Reprovar' }]

const store = useDocumentReviewStore()
const catalog = useProfessionalDocumentCatalogStore()
const {
  items,
  filters,
  isLoading,
  isUnavailable,
  loadError,
  hasActiveFilters,
  details,
  loadingDetailFor,
  isSaving
} = storeToRefs(store)
const toast = useToast()
const { notifyError } = useErrorToast()

const onlyInReview = ref(false)
const activeId = ref<number | null>(null)
const selectedIds = ref<number[]>([])
const listRef = ref<HTMLElement | null>(null)

const sequence = computed(() => flattenQueue(items.value, onlyInReview.value))
const groups = computed(() =>
  items.value
    .map((professional) => ({
      professional,
      entries: sequence.value.filter((entry) => entry.professional.userId === professional.userId)
    }))
    .filter((group) => group.entries.length > 0)
)
const inReviewCount = computed(() => sequence.value.filter(isInReview).length)
const position = computed(() =>
  sequence.value.findIndex((entry) => entry.document.documentId === activeId.value)
)
const active = computed<ReviewQueueEntry | null>(() => sequence.value[position.value] ?? null)
const activeDetail = computed(() =>
  active.value ? (details.value[active.value.professional.userId] ?? null) : null
)
const selectedEntries = computed(() =>
  sequence.value.filter((entry) => selectedIds.value.includes(entry.document.documentId))
)
const isFiltered = computed(() => hasActiveFilters.value || onlyInReview.value)

/** Foto do médico ativo (vem na fila); sem foto, o slot mostra o avatar padrão. */
const activeAvatarSrc = computed(() => resolveAvatarSrc(active.value?.professional.avatarUrl))

const professionalSubtitle = computed(() => {
  const professional = active.value?.professional
  if (!professional) return ''
  const crm = professional.crm ? `CRM ${professional.crm}/${professional.crmUf ?? ''}` : null
  return [crm, ...professional.companies.map((company) => company.name)].filter(Boolean).join(' · ')
})

const loadErrorMessage = computed(() => {
  const kept = 'Seus filtros foram mantidos.'
  if (!loadError.value || loadError.value === QUEUE_LOAD_ERROR) return kept
  return `${loadError.value.replace(/\.?$/, '.')} ${kept}`
})

const detailDocument = (entry: ReviewQueueEntry) =>
  details.value[entry.professional.userId]?.documents.find(
    (item) => item.documentId === entry.document.documentId
  ) ?? null

/**
 * Só Em Análise com arquivo. O detalhe, quando já carregou, manda (R19): é mais novo que a fila
 * e marca o documento restrito (D80, `currentVersion: null`), que não se aprova nem por atalho.
 */
function canDecide(entry: ReviewQueueEntry): boolean {
  if (!isInReview(entry) || entry.document.currentVersionId === null) return false
  const item = detailDocument(entry)
  return (
    !item || (item.status === ProfessionalDocumentStatus.IN_REVIEW && item.currentVersion !== null)
  )
}

/** Seleção só do que segue visível e decidível (filtro, refetch, decisão de outro revisor). */
const keepDecidable = (ids: number[], seq: ReviewQueueEntry[]) =>
  ids.filter((id) => seq.some((entry) => entry.document.documentId === id && canDecide(entry)))

watch(
  sequence,
  (seq) => {
    const kept = keepDecidable(selectedIds.value, seq)
    if (kept.length !== selectedIds.value.length) selectedIds.value = kept
    if (seq.some((entry) => entry.document.documentId === activeId.value)) return
    activeId.value = firstInReviewId(seq)
  },
  { immediate: true }
)

watch(
  () => active.value?.professional.userId,
  (userId) => {
    if (userId === undefined) return
    store.fetchProfessional(userId).catch((error) => notifyError(error, DETAIL_LOAD_ERROR))
  },
  { immediate: true }
)

// Com J/K o item ativo pode sair da área visível da lista (rolagem própria de 640px).
watch(activeId, async (id) => {
  await nextTick()
  const item = listRef.value?.querySelector(`[data-testid="document-queue-item-${id}"]`)
  // `?.()`: o jsdom dos testes não implementa `scrollIntoView`.
  item?.scrollIntoView?.({ block: 'nearest' })
})

function move(delta: number) {
  const target = sequence.value[position.value + delta]
  if (target) activeId.value = target.document.documentId
}

function toggleSelection(documentId: number) {
  selectedIds.value = selectedIds.value.includes(documentId)
    ? selectedIds.value.filter((id) => id !== documentId)
    : [...selectedIds.value, documentId]
}

function clearFilters() {
  onlyInReview.value = false
  if (hasActiveFilters.value) void store.setFilters({ ...EMPTY_REVIEW_FILTERS })
}

// --- decisões --------------------------------------------------------------------
const actionEntries = ref<ReviewQueueEntry[]>([])
const approveOpen = ref(false)
const rejectOpen = ref(false)
const undoTarget = ref<(DecisionTarget & { label: string }) | null>(null)

const approveItems = computed<ApproveModalItem[]>(() =>
  actionEntries.value.map((entry) => ({
    versionId: entry.document.currentVersionId as number,
    userId: entry.professional.userId,
    documentName: entry.document.name,
    professionalName: entry.professional.name,
    crmLabel: entry.professional.crm
      ? `CRM ${entry.professional.crm}/${entry.professional.crmUf ?? ''}`
      : null,
    hasExpiry: entry.document.hasExpiry,
    acceptsAuthenticityCode: entry.document.acceptsAuthenticityCode,
    description: detailDocument(entry)?.description ?? null
  }))
)
const rejectItems = computed<RejectModalItem[]>(() =>
  actionEntries.value.map((entry) => ({
    versionId: entry.document.currentVersionId as number,
    userId: entry.professional.userId,
    documentName: entry.document.name,
    professionalName: entry.professional.name
  }))
)
const undoDescription = computed(() =>
  undoTarget.value
    ? `${undoTarget.value.label} volta para Em Análise. A decisão desfeita fica no histórico.`
    : ''
)

function openApprove(entries: ReviewQueueEntry[]) {
  actionEntries.value = entries.filter(canDecide)
  if (actionEntries.value.length) approveOpen.value = true
}
function openReject(entries: ReviewQueueEntry[]) {
  actionEntries.value = entries.filter(canDecide)
  if (actionEntries.value.length) rejectOpen.value = true
}

/**
 * A store já recarregou fila e detalhes. O próximo sai da ordem que o revisor via (`before`, D23),
 * pulando o que acabou de ser decidido e o que deixou de estar Em Análise (outro revisor).
 * Na seleção ficam só os que falharam e seguem decidíveis (R10).
 */
function afterDecision(
  result: BulkDecisionResponse,
  verb: DecisionVerb,
  before: ReviewQueueEntry[],
  from: number
) {
  const decided = actionEntries.value
    .filter((entry) => result.succeeded.includes(entry.document.currentVersionId as number))
    .map((entry) => entry.document.documentId)
  const stillOpen = new Set(
    sequence.value.filter(canDecide).map((entry) => entry.document.documentId)
  )
  const closed = before.map((entry) => entry.document.documentId).filter((id) => !stillOpen.has(id))
  const next = nextInReviewId(before, from, [...decided, ...closed])
  if (next !== null) activeId.value = next
  selectedIds.value = keepDecidable(
    selectedIds.value.filter((id) => !decided.includes(id)),
    sequence.value
  )
  const summary = decisionSummary(result, verb)
  toast[summary.tone](summary.message)
}

/** Erro (ex.: 409 de decisão concorrente): mostra a mensagem e fica no mesmo documento. */
async function decide(
  verb: DecisionVerb,
  run: () => Promise<BulkDecisionResponse>,
  fallback: string
) {
  const before = sequence.value
  const from = position.value
  let result: BulkDecisionResponse
  try {
    result = await run()
  } catch (error) {
    notifyError(error, fallback)
    return
  } finally {
    approveOpen.value = false
    rejectOpen.value = false
  }
  afterDecision(result, verb, before, from)
}

function confirmApprove(targets: ApproveTarget[]) {
  return decide('aprovado', () => store.approve(targets), APPROVE_ERROR)
}

function confirmReject(targets: DecisionTarget[], payload: RejectVersionPayload) {
  return decide('reprovado', () => store.reject(targets, payload), REJECT_ERROR)
}

function requestUndo(versionId: number) {
  const entry = active.value
  if (!entry) return
  undoTarget.value = {
    versionId,
    userId: entry.professional.userId,
    label: `${entry.document.name} de ${entry.professional.name}`
  }
}

async function confirmUndo() {
  const target = undoTarget.value
  if (!target) return
  undoTarget.value = null
  try {
    await store.undo(target)
    toast.info('Decisão desfeita — documento voltou para Em Análise')
  } catch (error) {
    notifyError(error, UNDO_ERROR)
  }
}

useDocumentReviewShortcuts({
  isEnabled: () =>
    !isLoading.value &&
    !isSaving.value &&
    !approveOpen.value &&
    !rejectOpen.value &&
    undoTarget.value === null &&
    active.value !== null,
  onNext: () => move(1),
  onPrevious: () => move(-1),
  onApprove: () => active.value && openApprove([active.value]),
  onReject: () => active.value && openReject([active.value])
})

// Na criação (não no `onMounted`): o primeiro render já sai com o skeleton, sem piscar "Fila zerada".
void store.fetchQueue()
catalog.ensureLoaded().catch((error) => notifyError(error, CATALOG_LOAD_ERROR))
</script>
